import RoutePerformance from "./RoutePerformance";
import { EmergencyLoanAlert } from "./EmergencyLoanAlert";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatPercent, formatNumber } from "@/lib/utils";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Minus, RefreshCw, Trophy,
  DollarSign, Gauge, Star, Plane, Users, BarChart3
} from "lucide-react";

export default function DashboardTab({ game, team, kpis, onRefresh }) {
  const { t } = useTranslation();
  const [leaderboard, setLeaderboard] = useState([]);
  const [news, setNews] = useState([]);
  const [emergencyLoan, setEmergencyLoan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [game?.id]);

  useEffect(() => {
    if (!team?.id) return;
    axios.get(`${API}/teams/${team.id}/financials`)
      .then(res => setEmergencyLoan(res.data.emergency_loan))
      .catch(() => setEmergencyLoan(null));
  }, [team?.id, game?.current_quarter]);

  const fetchDashboardData = async () => {
    if (!game?.id) return;
    
    try {
      const [leaderboardRes, newsRes] = await Promise.all([
        axios.get(`${API}/games/${game.id}/leaderboard`),
        axios.get(`${API}/games/${game.id}/news`)
      ]);
      setLeaderboard(leaderboardRes.data);
      setNews(newsRes.data);
    } catch {
      // Dashboard data unavailable — non-blocking
    } finally {
      setLoading(false);
    }
  };

  const kpiConfig = [
    { key: "stock_price", label: t("dashboard.stockPrice"), format: "currency", icon: TrendingUp, good: 25, bad: 10 },
    { key: "net_profit", label: t("dashboard.netProfit"), format: "currency", icon: DollarSign, good: 100000, bad: -200000 },
    { key: "quality_rating", label: t("dashboard.quality"), format: "number", icon: Star, good: 75, bad: 50 },
    { key: "reliability", label: t("dashboard.reliability"), format: "percent", icon: Gauge, good: 85, bad: 65 },
    { key: "load_factor", label: t("dashboard.loadFactor"), format: "percent", icon: Users, good: 80, bad: 50 },
    { key: "cumulative_profit", label: t("dashboard.cumulativeProfit"), format: "currency", icon: BarChart3, good: 500000, bad: -500000 },
  ];

  const getKPIStatus = (value, config) => {
    if (value >= config.good) return "success";
    if (value <= config.bad) return "danger";
    return "neutral";
  };

  const formatValue = (value, format) => {
    switch (format) {
      case "currency":
        return formatCurrency(value);
      case "percent":
        return formatPercent(value);
      default:
        return formatNumber(value, 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">{t("nav.dashboard")}</h2>
          <p className="text-slate-500">{t("history.quarter")} {game?.current_quarter} - {t("dashboard.overview")}</p>
        </div>
        <Button variant="outline" onClick={onRefresh} className="gap-2" data-testid="refresh-dashboard-btn">
          <RefreshCw className="w-4 h-4" />
          {t("common.refresh")}
        </Button>
      </div>

      <EmergencyLoanAlert data={emergencyLoan} />

      {/* KPI Grid */}
      <div className="kpi-grid">
        {kpiConfig.map((config, index) => {
          const value = kpis?.[config.key] ?? 0;
          const status = getKPIStatus(value, config);
          const Icon = config.icon;
          
          return (
            <KPITile
              key={config.key}
              label={config.label}
              value={formatValue(value, config.format)}
              status={status}
              icon={Icon}
              delay={index}
            />
          );
        })}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Leaderboard */}
        <div className="lg:col-span-8">
          <Card>
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                <CardTitle className="font-heading">{t("dashboard.leaderboard")}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("dashboard.rank")}</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("dashboard.airline")}</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("dashboard.stockPrice")}</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("dashboard.profit")}</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("dashboard.quality")}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry) => (
                    <tr 
                      key={entry.team_id}
                      className={`border-b border-slate-50 ${entry.team_id === team?.id ? "bg-sky-50" : "hover:bg-slate-50"}`}
                      data-testid={`leaderboard-row-${entry.rank}`}
                    >
                      <td className="py-3 px-4">
                        <RankBadge rank={entry.rank} />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{entry.name}</span>
                          <span className="text-xs text-slate-400 font-mono">{entry.airline_code}</span>
                          {entry.is_ai && (
                            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">AI</span>
                          )}
                          {entry.team_id === team?.id && (
                            <span className="text-xs bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded">{t("common.you")}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-slate-700">
                        {formatCurrency(entry.stock_price, 2)}
                      </td>
                      <td className={`py-3 px-4 text-right font-mono ${entry.cumulative_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {formatCurrency(entry.cumulative_profit)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-sky-500 rounded-full"
                              style={{ width: `${entry.quality_rating}%` }}
                            />
                          </div>
                          <span className="text-sm text-slate-600 font-mono w-10">
                            {entry.quality_rating?.toFixed(0)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* News & Team Status */}
        <div className="lg:col-span-4 space-y-6">
          {/* Team Status */}
          <Card>
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2">
                <Plane className="w-5 h-5 text-sky-500" />
                <CardTitle className="font-heading">{t("company.fleetStatus")}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{t("dashboard.aircraft")}</span>
                  <span className="font-mono font-semibold text-slate-900">{team?.aircraft_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{t("dashboard.cash")}</span>
                  <span className="font-mono font-semibold text-slate-900">{formatCurrency(team?.cash || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">{t("company.totalEquity")}</span>
                  <span className="font-mono font-semibold text-slate-900">{formatCurrency(team?.total_equity || 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Industry News */}
          <Card>
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading">{t("dashboard.industryNews")}</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {news.map((item, index) => (
                  <div key={item.id} className={`${index > 0 ? "pt-4 border-t border-slate-100" : ""}`}>
                    <p className="text-xs text-sky-600 font-medium uppercase tracking-wider mb-1">
                      {item.type}
                    </p>
                    <p className="font-medium text-slate-800">{item.title}</p>
                    <p className="text-sm text-slate-500 mt-1">{item.content}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Route Performance */}
      <RoutePerformance team={team} />
    </div>
  );
}

function KPITile({ label, value, status, icon: Icon, delay }) {
  const statusClasses = {
    success: "kpi-tile-success",
    danger: "kpi-tile-danger",
    neutral: "kpi-tile-neutral",
    default: "kpi-tile-default"
  };

  const iconColors = {
    success: "text-emerald-500",
    danger: "text-rose-500",
    neutral: "text-teal-500",
    default: "text-slate-400"
  };

  return (
    <div 
      className={`p-4 rounded-lg border animate-fade-in stagger-${delay + 1} ${statusClasses[status]}`}
      data-testid={`kpi-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
        <Icon className={`w-4 h-4 ${iconColors[status]}`} />
      </div>
      <p className="text-2xl font-mono font-bold kpi-value">{value}</p>
    </div>
  );
}

function RankBadge({ rank }) {
  const classes = {
    1: "bg-amber-400 text-amber-900",
    2: "bg-slate-300 text-slate-700",
    3: "bg-amber-600 text-white"
  };

  return (
    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${classes[rank] || "bg-slate-100 text-slate-600"}`}>
      {rank}
    </span>
  );
}
